import React, { useState } from 'react';
import { copyText } from '../utils/clipboard';

const HEIGHTS = [400, 600, 800];


export const buildEmbedUrl = (origin, pollRef, { question, chart, showQr, showHeader }) => {
    const params = new URLSearchParams();
    if (question) params.set('q', String(question));
    if (chart) params.set('chart', chart);
    if (showQr) params.set('qr', '1');
    if (!showHeader) params.set('header', '0');
    const query = params.toString();
    return `${origin}/embed/${pollRef}${query ? `?${query}` : ''}`;
};

export const buildEmbedCode = (url, height, title) => {
    const safeTitle = String(title || 'Результаты опроса').replace(/[&"<>]/g, (ch) => (
        { '&': '&amp;', '"': '&quot;', '<': '&lt;', '>': '&gt;' }[ch]
    ));
    return `<iframe src="${url}" width="100%" height="${height}" style="border:0;max-width:100%;" `
        + `title="${safeTitle}" loading="lazy" allowfullscreen></iframe>`;
};

const EmbedPanel = ({ poll, pollRef }) => {
    const [question, setQuestion] = useState(0);
    const [chart, setChart] = useState('');
    const [height, setHeight] = useState(600);
    const [showQr, setShowQr] = useState(true);
    const [showHeader, setShowHeader] = useState(true);
    const [copied, setCopied] = useState(null);

    const questions = poll.questions || [];
    const url = buildEmbedUrl(window.location.origin, pollRef, { question, chart, showQr, showHeader });
    const code = buildEmbedCode(url, height, poll.title);

    const handleCopy = async (what, text) => {
        // Без всплывающих окон: кнопка сама показывает результат
        const ok = await copyText(text);
        setCopied(ok ? what : `${what}-fail`);
        setTimeout(() => setCopied((c) => (c === what || c === `${what}-fail` ? null : c)), ok ? 2000 : 4000);
    };

    return (
        <div className="poll-card__panel embed-panel">
            <h5 className="poll-card__panel-title">Встроить на сайт, в LMS или вебинар</h5>
            <p className="embed-panel__lead">
                Результаты обновляются в реальном времени прямо на странице, куда вставлен код. Зрители могут сами переключать вопросы и тип диаграммы.
            </p>

            <div className="embed-panel__options">
                <label className="embed-panel__field">
                        <span>Вопросы</span>
                        <select className="poll-card__input" value={question} onChange={(e) => setQuestion(Number(e.target.value))}>
                            <option value={0}>Все вопросы (по одному, с переключением)</option>
                            {questions.map((q, i) => (
                                <option key={q.id || i} value={i + 1}>
                                    {`№${i + 1}: ${q.text.length > 50 ? `${q.text.slice(0, 50)}…` : q.text}`}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="embed-panel__field">
                        <span>Тип диаграммы</span>
                        <select className="poll-card__input" value={chart} onChange={(e) => setChart(e.target.value)}>
                            <option value="">Автоматически</option>
                            <option value="bar">Вертикальная</option>
                            <option value="horizontal">Горизонтальная</option>
                            <option value="pie">Круговая</option>
                        </select>
                    </label>

                <label className="embed-panel__field">
                    <span>Высота окна</span>
                    <select className="poll-card__input" value={height} onChange={(e) => setHeight(Number(e.target.value))}>
                        {HEIGHTS.map((h) => <option key={h} value={h}>{h} px</option>)}
                    </select>
                </label>
            </div>

            <div className="embed-panel__checks">
                    <label>
                        <input type="checkbox" checked={showHeader} onChange={(e) => setShowHeader(e.target.checked)} />
                        Название опроса и счётчик ответов
                    </label>
                    <label>
                        <input type="checkbox" checked={showQr} onChange={(e) => setShowQr(e.target.checked)} />
                        QR-код и ссылка для голосования (пока опрос активен)
                    </label>
                </div>

            <div className="poll-card__link-block">
                <label className="poll-card__link-label" htmlFor={`embed-code-${poll.id}`}>Код для вставки</label>
                <textarea
                    id={`embed-code-${poll.id}`}
                    className="poll-card__input embed-panel__code"
                    value={code}
                    readOnly
                    rows={3}
                    onFocus={(e) => e.target.select()}
                />
                <div className="embed-panel__buttons">
                    <button type="button" className="pc-btn pc-btn--activate" onClick={() => handleCopy('code', code)}>
                        {copied === 'code' ? 'Скопировано ✓' : copied === 'code-fail' ? 'Выделите код и нажмите Ctrl+C' : 'Копировать код'}
                    </button>
                    <button type="button" className="pc-btn pc-btn--qr" onClick={() => handleCopy('url', url)}>
                        {copied === 'url' ? 'Скопировано ✓' : copied === 'url-fail' ? 'Выделите ссылку и нажмите Ctrl+C' : 'Копировать ссылку'}
                    </button>
                    <a className="pc-btn pc-btn--links" href={url} target="_blank" rel="noopener noreferrer">
                        Предпросмотр
                    </a>
                </div>
            </div>

            <details className="embed-panel__help">
                <summary>Как вставить: сайт, Moodle, вебинарная комната</summary>
                <ul>
                    <li>
                        <strong>Сайт.</strong> Tilda — блок «Другое → T123 HTML-код»; WordPress — блок «Произвольный HTML»;
                        конструкторы и CMS — элемент «HTML / Embed / Код». Вставьте код и опубликуйте страницу.
                    </li>
                    <li>
                        <strong>Moodle.</strong> Добавьте элемент «Страница» или «Пояснение» (Метка), в редакторе нажмите
                        «&lt;/&gt;» (HTML-код) и вставьте код. Если окно исчезает после сохранения — попросите администратора
                        Moodle разрешить iframe (доверенное содержимое) или добавьте ресурс «Гиперссылка» с этой ссылкой
                        и в настройках «Внешний вид» выберите «Встроить».
                    </li>
                    <li>
                        <strong>Вебинарная комната.</strong> Если платформа умеет показывать веб-страницу или HTML-виджет —
                        вставьте код или ссылку. Если нет — откройте предпросмотр и покажите его через демонстрацию экрана.
                    </li>
                </ul>
            </details>
        </div>
    );
};

export default EmbedPanel;
